import React, { useEffect, useRef } from 'react';
import { Play, Target, TrendingUp, Zap, Award, ArrowRight, CheckCircle, BarChart3, Sparkles, Star } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';



gsap.registerPlugin(ScrollTrigger);

const AmazonAdsPage = () => {
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const floatingElementsRef = useRef([]);
  const statsRef = useRef([]);
  const sectionsRef = useRef([]);
  const cardsRef = useRef([]);
  const orbsRef = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    const hero = heroRef.current;
    const floatingElements = floatingElementsRef.current;
    const stats = statsRef.current;
    const sections = sectionsRef.current;
    const cards = cardsRef.current;
    const orbs = orbsRef.current;

    // Floating orbs animation
    orbs.forEach((orb, index) => {
      gsap.to(orb, {
        y: index % 2 === 0 ? -50 : 50,
        x: index % 2 === 0 ? 30 : -30,
        rotation: 360,
        duration: 8 + index * 2,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
      });
    });

    // Hero section animation
    gsap.fromTo(hero.querySelector('.hero-content'),
      { opacity: 0, y: 100, scale: 0.9 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.5,
        ease: "power3.out",
        delay: 0.5
      }
    );

    // Floating elements animation
    floatingElements.forEach((element, index) => {
      gsap.to(element, {
        y: -20,
        rotation: 360,
        duration: 4 + index,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
        delay: index * 0.5
      });
    });

    // Stats counter animation
    const animateStats = () => {
      const targets = [300, 85, 150, 91];
      stats.forEach((stat, index) => {
        const counter = { value: 0 };
        gsap.to(counter, {
          value: targets[index],
          duration: 2,
          ease: "power2.out",
          onUpdate: () => {
            stat.textContent = Math.floor(counter.value);
          }
        });
      });
    };

    // Stats section scroll trigger
    ScrollTrigger.create({
      trigger: ".stats-section",
      start: "top 80%",
      onEnter: animateStats,
      once: true
    });

    // Sections stagger animation
    sections.forEach((section, index) => {
      gsap.fromTo(section,
        { opacity: 0, y: 80, rotationX: 45 },
        {
          opacity: 1,
          y: 0,
          rotationX: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    // Cards animation
    cards.forEach((card, index) => {
      gsap.fromTo(card,
        { opacity: 0, y: 60, scale: 0.8 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay: index * 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 90%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const addFloatingRef = (el) => {
    if (el && !floatingElementsRef.current.includes(el)) {
      floatingElementsRef.current.push(el);
    }
  };

  const addStatsRef = (el) => {
    if (el && !statsRef.current.includes(el)) {
      statsRef.current.push(el);
    }
  };

  const addSectionRef = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  const addCardRef = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  const addOrbRef = (el) => {
    if (el && !orbsRef.current.includes(el)) {
      orbsRef.current.push(el);
    }
  };

  return (
    <>

      <div
        ref={containerRef}
        style={{
          position: "relative",
          background: "linear-gradient(180deg, #000 0%, #0a0a0a 50%, #000 100%)",
          color: "#fff",
          overflow: "hidden",
          paddingTop: "0px",
        }}
      >
        {/* Background Orbs */}
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "600px",
            height: "600px",
            background: "radial-gradient(circle, rgba(255, 215, 0, 0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "10%",
            right: "-15%",
            filter: "blur(120px)",
            zIndex: 1,
          }}
        />
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(0, 217, 255, 0.06) 0%, transparent 70%)",
            borderRadius: "50%",
            bottom: "20%",
            left: "-10%",
            filter: "blur(100px)",
            zIndex: 1,
          }}
        />
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(147, 51, 234, 0.05) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            filter: "blur(80px)",
            zIndex: 1,
          }}
        />

        {/* Floating Particles */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `
              radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.1), transparent),
              radial-gradient(2px 2px at 40px 70px, rgba(255, 215, 0, 0.1), transparent),
              radial-gradient(1px 1px at 90px 40px, rgba(0, 217, 255, 0.1), transparent),
              radial-gradient(1px 1px at 130px 80px, rgba(147, 51, 234, 0.1), transparent)
            `,
            backgroundRepeat: "repeat",
            backgroundSize: "150px 100px",
            animation: "particleFloat 20s linear infinite",
            zIndex: 1,
          }}
        />

        {/* Floating Background Elements */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
          }}
        >
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "20%",
              left: "10%",
              width: "60px",
              height: "60px",
              background: "rgba(255, 215, 0, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFD700",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 215, 0, 0.2)",
            }}
          >
            <Target size={24} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "30%",
              right: "15%",
              width: "70px",
              height: "70px",
              background: "rgba(0, 217, 255, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#00D9FF",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(0, 217, 255, 0.2)",
            }}
          >
            <TrendingUp size={32} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "60%",
              left: "5%",
              width: "65px",
              height: "65px",
              background: "rgba(147, 51, 234, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9333EA",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(147, 51, 234, 0.2)",
            }}
          >
            <Zap size={28} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "70%",
              right: "20%",
              width: "55px",
              height: "55px",
              background: "rgba(255, 215, 0, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFD700",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 215, 0, 0.2)",
            }}
          >
            <Award size={26} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "40%",
              left: "80%",
              width: "68px",
              height: "68px",
              background: "rgba(0, 217, 255, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#00D9FF",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(0, 217, 255, 0.2)",
            }}
          >
            <BarChart3 size={30} />
          </div>
        </div>

        {/* Hero Section */}
        <section
          ref={heroRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "8rem 2rem 6rem",
            textAlign: "center",
          }}
        >
          <div
            className="hero-content"
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            {/* Hero Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.75rem 1.5rem",
                background: "rgba(0, 0, 0, 0.6)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 215, 0, 0.4)",
                borderRadius: "50px",
                fontSize: "0.95rem",
                fontWeight: "600",
                marginBottom: "3rem",
                color: "#fff",
                boxShadow: "0 4px 15px rgba(255, 215, 0, 0.15)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: "-50%",
                  width: "50%",
                  height: "100%",
                  background: "linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.5), transparent)",
                  transform: "skewX(-20deg)",
                  animation: "shimmer 3s infinite",
                }}
              />
              <Sparkles size={18} style={{ color: "#FFD700" }} />
              Amazon Ads Excellence
            </div>

            <h1
              style={{
                fontSize: "clamp(3rem, 6vw, 5rem)",
                fontWeight: "900",
                lineHeight: "1.1",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              Turn Product Listings into{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #FFD700, #00D9FF, #9333EA)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: "gradientShift 3s ease-in-out infinite",
                }}
              >
                Top Performers
              </span>
            </h1>

            <p
              style={{
                fontSize: "1.3rem",
                fontWeight: "600",
                color: "#FFD700",
                marginBottom: "1.5rem",
              }}
            >
              Amazon Ads Excellence by Beyond the Analytics
            </p>

            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "900px",
                margin: "0 auto 3rem",
              }}
            >
              With more competition than ever, simply listing your product isn't enough. At Beyond the Analytics, 
              our Amazon Ads experts use data-backed strategies, keyword intelligence, and precision targeting to 
              help you win the buy box, boost visibility across the marketplace, and drive profitable growth.
            </p>
               <Link to="/consultation">
            <button
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.5rem 3rem",
                background: "linear-gradient(135deg, #FFD700, #00D9FF)",
                color: "#000",
                border: "none",
                borderRadius: "50px",
                fontSize: "1.2rem",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                boxShadow: "0 8px 25px rgba(255, 215, 0, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px) scale(1.05)";
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(255, 215, 0, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(255, 215, 0, 0.3)";
              }}
            >
              <Play size={24} />
           Grow with us 
              <ArrowRight size={24} />
            </button>

            </Link>
          </div>
        </section>

        {/* Stats Section */}
        <section
          className="stats-section"
          style={{
            position: "relative",
            zIndex: 10,
            padding: "4rem 2rem",
            background: "rgba(255, 255, 255, 0.02)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "24px",
            margin: "0 2rem 6rem",
            maxWidth: "1200px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.5), transparent)",
            }}
          />
          
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "3rem",
              textAlign: "center",
            }}
          >
            {[
              { number: 300, label: "% Average ROAS Increase", color: "#FFD700" },
              { number: 85, label: "% Click-Through Rate", color: "#00D9FF" },
              { number: 150, label: "+ Campaigns Managed", color: "#9333EA" },
              { number: 24, label: "Campaigns Completed", color: "#FFD700" },
            ].map((stat, index) => (
              <div key={index} style={{ position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    top: "-20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "40px",
                    height: "40px",
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Star size={20} style={{ color: "#000" }} />
                </div>
                <div
                  ref={addStatsRef}
                  style={{
                    fontSize: "3rem",
                    fontWeight: "900",
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    marginBottom: "0.5rem",
                    marginTop: "1rem",
                  }}
                >
                  0
                </div>
                <div
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why Amazon Ads Matter */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(0, 217, 255, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(0, 217, 255, 0.15)",
            }}
          >
            <Target size={18} style={{ color: "#00D9FF" }} />
            Strategic Importance
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            Why Amazon Ads{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #00D9FF, #9333EA)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Matter
            </span>
          </h2>

          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "24px",
              padding: "3rem",
              marginBottom: "3rem",
            }}
          >
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                marginBottom: "2rem",
                maxWidth: "800px",
                margin: "0 auto 2rem",
              }}
            >
              Amazon is more than just the world's largest eCommerce platform; it's a powerful advertising engine 
              that puts your products in front of high-intent shoppers. With placements across search results, 
              product pages, and display networks, Amazon Ads connect your brand to people ready to buy.
            </p>
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "800px",
                margin: "0 auto",
              }}
            >
              With millions of daily product searches and real-time performance data, Amazon Ads turn visibility 
              into conversions—whether you're looking to grow sales, boost rankings, or increase brand awareness.
            </p>
          </div>
       <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #00D9FF, #9333EA)",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(0, 217, 255, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Advertise on Amazon
            <ArrowRight size={20} />
          </button>
          </Link>
        </section>

        {/* Services Section */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(147, 51, 234, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(147, 51, 234, 0.15)",
            }}
          >
            <Award size={18} style={{ color: "#9333EA" }} />
            Premium Services
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "1rem",
              color: "#fff",
            }}
          >
            Amazon Ads Services That Go{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #9333EA, #FFD700)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Beyond the Basics
            </span>
          </h2>

          <p
            style={{
              fontSize: "1.2rem",
              fontWeight: "600",
              color: "#9333EA",
              marginBottom: "2rem",
            }}
          >
            From Beyond the Analytics
          </p>

          <p
            style={{
              fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
              color: "rgba(255, 255, 255, 0.8)",
              lineHeight: "1.7",
              maxWidth: "900px",
              margin: "0 auto 4rem",
            }}
          >
            With more competition and more complexity on the platform than ever before, you need more than just ads. 
            You need a partner who understands performance, scale, and precision. At Beyond the Analytics, we help you 
            launch, manage, and continuously optimise Amazon Ads campaigns that do more than get clicks; they drive real business growth.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
              marginBottom: "4rem",
            }}
          >
            {[
              "Align Amazon Ads with your full-funnel PPC strategy",
              "Drive more visibility and more conversions for your products",
              "Choose the right ad types for your goals, scale, and budget",
              "Spend less time on the dashboard and more time running your business",
            ].map((achievement, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "1.5rem",
                  background: "rgba(255, 255, 255, 0.02)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.transform = "translateX(10px)";
                  e.currentTarget.style.borderColor = "rgba(147, 51, 234, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                  e.currentTarget.style.transform = "translateX(0)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "linear-gradient(135deg, #9333EA, #FFD700)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle size={20} style={{ color: "#000" }} />
                </div>
                <span
                  style={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {achievement}
                </span>
              </div>
            ))}
          </div>
        <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #9333EA, #FFD700)",
              color: "#000",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(147, 51, 234, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Request a Custom Quote
            <ArrowRight size={20} />
          </button>
          </Link> 
        </section>

        {/* Ad Types */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "4rem",
              color: "#fff",
            }}
          >
            Ad Types We Use to{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #FFD700, #00D9FF)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Grow Your Brand
            </span>
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "2rem",
            }}
          >
            {[
              {
                icon: Target,
                title: "Sponsored Products",
                description: "Promote individual listings in Amazon's search results with targeted placement that drives immediate visibility and conversions.",
                color: "#FFD700",
              },
              {
                icon: Award,
                title: "Sponsored Brands",
                description: "Highlight your brand and product suite at the top of key pages, building brand awareness and driving traffic to your storefront.",
                color: "#00D9FF",
              },
              {
                icon: TrendingUp,
                title: "Sponsored Display",
                description: "Reach shoppers beyond Amazon with targeted display ads that retarget customers and expand your reach across the web.",
                color: "#9333EA",
              },
              {
                icon: Play,
                title: "Video Ads",
                description: "Engage customers through streaming and in-feed video placements that showcase your products in action.",
                color: "#FFD700",
              },
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  ref={addCardRef}
                  style={{
                    position: "relative",
                    background: "rgba(255, 255, 255, 0.02)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "24px",
                    padding: "2.5rem",
                    cursor: "pointer",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-10px) scale(1.02)";
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.borderColor = `${service.color}40`;
                    e.currentTarget.style.boxShadow = `0 20px 40px ${service.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "1px",
                      background: `linear-gradient(90deg, transparent, ${service.color}80, transparent)`,
                    }}
                  />

                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      background: `linear-gradient(135deg, ${service.color}, ${service.color}80)`,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "2rem",
                      color: "#000",
                      boxShadow: `0 8px 25px ${service.color}40`,
                    }}
                  >
                    <IconComponent size={32} />
                  </div>

                  <h3
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: "700",
                      marginBottom: "1rem",
                      color: "#fff",
                      lineHeight: "1.3",
                    }}
                  >
                    {service.title}
                  </h3>
                  <p
                    style={{
                      color: "rgba(255, 255, 255, 0.7)",
                      lineHeight: "1.7",
                      fontSize: "1rem",
                    }}
                  >
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Performance Tuning */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            <span
              style={{
                background: "linear-gradient(135deg, #FFD700, #00D9FF)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Relentless
            </span>{" "}
            Performance Tuning
          </h2>

          <p
            style={{
              fontSize: "1.2rem",
              color: "rgba(255, 255, 255, 0.8)",
              marginBottom: "4rem",
            }}
          >
            We handle the behind-the-scenes grind:
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: "2rem",
              marginBottom: "3rem",
            }}
          >
            {[
              {
                icon: Zap,
                title: "Bidding and budget adjustments",
                description: "Continuous optimization based on real-time performance data",
                color: "#FFD700",
              },
              {
                icon: BarChart3,
                title: "Target expansion through weekly data reviews",
                description: "Identify new opportunities and scale winning strategies",
                color: "#00D9FF",
              },
              {
                icon: Target,
                title: "Search term and performance report analysis",
                description: "Deep insights that drive strategic decision-making",
                color: "#9333EA",
              },
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  ref={addCardRef}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1.5rem",
                    padding: "2rem",
                    background: "rgba(255, 255, 255, 0.02)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "20px",
                    textAlign: "left",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.transform = "translateX(10px)";
                    e.currentTarget.style.borderColor = `${item.color}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                    e.currentTarget.style.transform = "translateX(0)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  }}
                >
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      background: `linear-gradient(135deg, ${item.color}, ${item.color}80)`,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#000",
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent size={28} />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "700",
                        marginBottom: "0.5rem",
                        color: "#fff",
                      }}
                    >
                      {item.title}
                    </h4>
                    <p
                      style={{
                        color: "rgba(255, 255, 255, 0.7)",
                        lineHeight: "1.6",
                      }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "20px",
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <p
              style={{
                fontSize: "1.2rem",
                fontWeight: "600",
                color: "#fff",
                background: "linear-gradient(135deg, #FFD700, #00D9FF)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              So you can scale confidently while we fine-tune for profit.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "32px",
              padding: "4rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "2px",
                background: "linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.8), rgba(0, 217, 255, 0.8), transparent)",
              }}
            />

            <h2
              style={{
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: "900",
                lineHeight: "1.2",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              Let's Build Your Amazon{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #FFD700, #00D9FF, #9333EA)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: "gradientShift 3s ease-in-out infinite",
                }}
              >
                Growth Engine
              </span>
            </h2>

            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "700px",
                margin: "0 auto 3rem",
              }}
            >
              Book a free consultation with our team. We'll walk you through how to make your Amazon Ads 
              work harder, smarter, and faster.
            </p>
             <Link to="/consultation">
            <button
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.5rem 3rem",
                background: "linear-gradient(135deg, #FFD700, #00D9FF, #9333EA)",
                backgroundSize: "200% 100%",
                color: "#000",
                border: "none",
                borderRadius: "50px",
                fontSize: "1.2rem",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                boxShadow: "0 10px 30px rgba(255, 215, 0, 0.3)",
                animation: "gradientShift 3s ease-in-out infinite",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px) scale(1.05)";
                e.currentTarget.style.boxShadow = "0 20px 50px rgba(255, 215, 0, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(255, 215, 0, 0.3)";
              }}
            >
              <Play size={24} />
              Request a Custom Quote
              <ArrowRight size={24} />
            </button>
            </Link> 
          </div>
        </section>
      </div>

      <style jsx>{`
        @keyframes particleFloat {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-100px); }
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes shimmer {
          0% { left: -50%; }
          100% { left: 120%; }
        }

        @media (max-width: 768px) {
          section {
            padding: 3rem 1rem !important;
          }
        }
      `}</style>

    
    </>
  );
};

export default AmazonAdsPage;